import React, {useEffect, useState} from 'react';
import {Button, Header, Icon, Label, Pagination, Table} from 'semantic-ui-react';
import {useLocation, useNavigate} from "react-router-dom";
import moment from "moment/moment";
import {toast, ToastOptions} from "react-toastify";
import {BonusConfig} from "../../../../../../models/bonusConfig";
import {BonusConfigsFilter} from "../../../../../../filters/bonus-configs-filter";
import {SessionUser} from "../../../../../../models/session-user";
import {useBlockUI} from "../../../../../../shared/services/block-ui-service";
import SessionStorage from "../../../../../../store/session";
import useBonusConfigService from "../../../../../../services/backoffice/hooks/useBonusConfigService";
import {ToasterConfig} from "../../../../../../config/ToasterConfig";
import {EBonusConfigType} from "../../../../../../enums/EBonusConfigType";
import StringUtils from "../../../../../../shared/utils/string-utils";
import TooltipInfo from "../../../../../../components/tooltip/tooltip";

interface Props {
    filterType: string | number;
    filterFleetId: string | null;
    filter: BonusConfigsFilter;
}

const BonusConfigListComponent: React.FC<Props> = ({filterType, filterFleetId, filter}) => {
    const [bonusConfigs, setBonusConfigs] = useState<BonusConfig[]>([]);
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [localFilter, setLocalFilter] = useState<BonusConfigsFilter>({type: filterType, fleetId: filterFleetId});
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const {getBonusConfigs, activateBonusConfig, inactivateBonusConfig, deleteBonusConfig} = useBonusConfigService();
    const [totalPages, setTotalPages] = useState<number>()
    const [showFilters, setShowFilters] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (filter.fleetId) return
        const params = new URLSearchParams(location.search);
        const id = params.get('operationId');
        setLocalFilter({...filter, fleetId: id});
    }, [location.search]);

    useEffect(() => {
        // Atualiza o filtro local sempre que o filtro vindo da página principal mudar
        setLocalFilter({
            ...filter,
            type: filterType,
            fleetId: filterFleetId ?? filter.fleetId,
        });
    }, [filter, filterType, filterFleetId]);

    const blockUI = useBlockUI();
    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user)
        }

        updateSessionUser()
    }, [])

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        listBonusConfigs();
    }, [localFilter]);

    async function listBonusConfigs() {
        setLoading(true)
        if (localFilter.type) {
            const response = await getBonusConfigs({
                ...localFilter,
            });
            setLoading(false)
            setTotalPages(response.totalPages);
            setBonusConfigs(response.itens);
        }
        setLoading(false)
    }


    const getBonusTypeLabel = (type: string): string => {
        const bonusType = EBonusConfigType.find(item => item.value === type);
        return bonusType ? bonusType.text : '';
    };


    const toggleActivationBonusConfig = async (bonusConfig: BonusConfig, activate?: boolean) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${bonusConfig.isActive ? 'desativar' : 'ativar'} a Premiação?`);
            if (!result) return
            setLoading(true);
            if (bonusConfig) {
                const result = !bonusConfig.isActive ? await activateBonusConfig(bonusConfig.id) : await inactivateBonusConfig(bonusConfig.id);
                if (!!result) {
                    toast.success(`A Premiação foi ${bonusConfig.isActive ? 'desativada' : 'ativada'} com sucesso`, ToasterConfig.success as ToastOptions);
                    listBonusConfigs()
                }
            }
            setLoading(false);
        } catch (error: any) {
            toast.warn(`Falha ao ${bonusConfig.isActive ? 'desativar' : 'ativar'} Premiação`, ToasterConfig.error as ToastOptions);
            setLoading(false);
            setError(error.message);
        }
    };

    async function deleteConfig(bonusConfig: BonusConfig) {
        try {
            const confirm = window.confirm(`Tem certeza que deseja apagar a Premiação?`);

            if (confirm) {
                if (bonusConfig) {
                    await deleteBonusConfig(bonusConfig.id);
                    toast.success('Premiação deletada com sucesso', ToasterConfig.success as ToastOptions);
                    listBonusConfigs()
                }
                setLoading(false);
            }
        } catch (error: any) {
            toast.warn(`Falha ao deletar Premiação`, ToasterConfig.error as ToastOptions);
            setLoading(false);
            setError(error.message);
        }
    }

    return (
        <div>
            {!!bonusConfigs.length && <Header as="h2" textAlign="center" color={'black'}>
                {bonusConfigs[0].fleetName}
            </Header>}
            <Header as="h3" textAlign="center" color={'green'}>
                {filterType === 'EngineLoad' && 'Premiações de Carga no Motor'}
                {filterType === 'Inertia' && 'Premiações de Inércia'}
                {filterType === 'EngineLoadException' && 'Premiações de Exceção de Carga no Motor'}
            </Header>

            <Table.Row>


                <Table.HeaderCell>
                    <Button content="Voltar para lista de operações"
                            icon='arrow left'
                            onClick={() =>
                                navigate('/backoffice/fleets')} grey/>
                </Table.HeaderCell>

                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button
                            color="green"
                            onClick={() =>
                                navigate(
                                    `/backoffice/fleets/bonus-configs/${filterType === 'EngineLoadException'
                                        ? 'engine-load-exception'
                                        : filterType === 'EngineLoad'
                                            ? 'engine-load'
                                            : 'inertia'
                                    }/create?fleetId=${filterFleetId}&tab=${filterType === 'EngineLoad'
                                        ? 'engineLoad'
                                        : filterType === 'EngineLoadException'
                                            ? 'engineLoadException'
                                            : 'inertia'}`
                                )
                            }
                        >
                            <Icon name="plus" /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>

            </Table.Row>

            <Table color={"green"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        {filterType === 'EngineLoadException' ? (
                            <>
                                <Table.HeaderCell>Placa</Table.HeaderCell>
                                <Table.HeaderCell>Criado em</Table.HeaderCell>
                                <Table.HeaderCell>Ativo</Table.HeaderCell>
                            </>
                        ) : (
                            <>
                                <Table.HeaderCell>Min. Performance (%)</Table.HeaderCell>
                                <Table.HeaderCell>Máx. Performance (%)</Table.HeaderCell>
                                <Table.HeaderCell>Tipo do Bônus</Table.HeaderCell>
                                <Table.HeaderCell>Valor do Bônus</Table.HeaderCell>
                                <Table.HeaderCell>Criado em</Table.HeaderCell>
                                <Table.HeaderCell>Ativo</Table.HeaderCell>
                            </>
                        )}
                    </Table.Row>
                </Table.Header>

                {(sessionUser?.userData?.profile === 'Admin' || sessionUser?.userData?.profile === 'Manager') && (
                    <Table.Body>
                        {bonusConfigs?.map((bonusConfig) => (
                            <Table.Row key={bonusConfig.id}>
                                <Table.Cell>
                                    <Button.Group>
                                        {/* Tooltip no botão de exclusão */}
                                        <TooltipInfo
                                            content="Clique para deletar os parametros de premiação"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => deleteConfig(bonusConfig)}
                                                    icon="broken chain"
                                                    content="Desvincular"
                                                />
                                            }
                                        />

                                        <TooltipInfo
                                            content={`Clique para ${bonusConfig.isActive ? 'desativar' : 'ativar'} os parametros de premiação`}
                                            trigger={
                                                <Button
                                                    color={bonusConfig.isActive ? 'red' : 'green'}
                                                    onClick={() => toggleActivationBonusConfig(bonusConfig)}
                                                    icon={bonusConfig.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />

                                    </Button.Group>
                                </Table.Cell>
                                {filterType === 'EngineLoadException' ? (
                                    <>
                                        <Table.Cell>{bonusConfig.vehiclePlate}</Table.Cell>
                                        <Table.Cell>{moment(bonusConfig.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                                        <Table.Cell>
                                            <Label
                                                key={bonusConfig.id}
                                                color={!bonusConfig.isActive ? 'red' : 'green'}
                                            >
                                                {bonusConfig.isActive ? 'Sim' : 'Não'}
                                            </Label>
                                        </Table.Cell>
                                    </>
                                ) : (
                                    <>
                                        <Table.Cell>{`${StringUtils.decimalToPercentSeparatedByComma(bonusConfig?.minPerformance)}`}</Table.Cell>
                                        <Table.Cell>{`${StringUtils.decimalToPercentSeparatedByComma(bonusConfig?.maxPerformance)}`}</Table.Cell>
                                        <Table.Cell>{getBonusTypeLabel(bonusConfig.type.toString())}</Table.Cell>
                                        <Table.Cell>{`${StringUtils.formatToBRL(bonusConfig?.bonusValue)}`}</Table.Cell>
                                        <Table.Cell>{moment(bonusConfig.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                                        <Table.Cell>
                                            <Label
                                                key={bonusConfig.id}
                                                color={!bonusConfig.isActive ? 'red' : 'green'}
                                            >
                                                {bonusConfig.isActive ? 'Sim' : 'Não'}
                                            </Label>
                                        </Table.Cell>
                                    </>
                                )}
                            </Table.Row>
                        ))}
                    </Table.Body>
                )}
            </Table>

            <Pagination totalPages={totalPages ?? 0}
                        defaultActivePage={localFilter?.Page ?? 1}
                        onPageChange={(e, {activePage}) => {
                            setLocalFilter({...localFilter, Page: activePage as number})
                        }
                        }>
            </Pagination>
        </div>
    );
};

export default BonusConfigListComponent;
